import RoundedCross from "@/components/icons/RoundedCross";
import Image from "next/image";
import {
  FileText,
  Video,
  FileAudio,
  FileArchive,
  FileCode,
  File,
  Download,
} from "lucide-react";

type FilePreviewProps = {
  url: string;
  name?: string;
  size?: string;
  showImagePreview?: boolean;
  showImagePreviewUrl?: string;
  removeFile?: () => void;
  showDeleteButton?: boolean;
};

type FileExtension =
  | "jpg"
  | "jpeg"
  | "png"
  | "gif"
  | "webp"
  | "mp4"
  | "webm"
  | "mov"
  | "mp3"
  | "wav"
  | "pdf"
  | "doc"
  | "docx"
  | "txt"
  | "csv"
  | "xlsx"
  | "js"
  | "ts"
  | "json"
  | "html"
  | "zip"
  | "rar";

const FilePreview = ({
  url,
  name,
  size,
  showDeleteButton,
  removeFile,
  showImagePreview,
  showImagePreviewUrl,
}: FilePreviewProps) => {
  // Extract file extension and name from URL
  const getFileInfo = () => {
    const urlWithoutParams = url.split("?")[0].split("#")[0];
    const parts = urlWithoutParams.split("/");
    const fileNameWithExt = parts[parts.length - 1];
    const fileParts = fileNameWithExt.split(".");
    const extension =
      fileParts.length > 1
        ? (fileParts.pop() || "unknown").toLowerCase()
        : "unknown";
    const baseName = fileParts.join(".") || "untitled";

    return {
      extension,
      fallbackName: baseName,
    };
  };

  const { extension, fallbackName } = getFileInfo();
  const displayName = name || fallbackName;

  // Map extensions to icons and colors
  const fileConfig: Record<
    FileExtension,
    { icon: React.ElementType; color: string }
  > = {
    // Images
    jpg: { icon: Image, color: "text-blue-500" },
    png: { icon: Image, color: "text-blue-500" },
    jpeg: { icon: Image, color: "text-blue-500" },
    gif: { icon: Image, color: "text-blue-500" },
    webp: { icon: Image, color: "text-blue-500" },

    // Videos
    mp4: { icon: Video, color: "text-purple-500" },
    webm: { icon: Video, color: "text-purple-500" },
    mov: { icon: Video, color: "text-purple-500" },

    // Audio
    mp3: { icon: FileAudio, color: "text-green-500" },
    wav: { icon: FileAudio, color: "text-green-500" },

    // Documents
    pdf: { icon: FileText, color: "text-red-500" },
    doc: { icon: FileText, color: "text-blue-600" },
    docx: { icon: FileText, color: "text-blue-600" },
    txt: { icon: FileText, color: "text-gray-600" },

    // Spreadsheets
    csv: { icon: FileText, color: "text-green-600" },
    xlsx: { icon: FileText, color: "text-green-600" },

    // Code
    js: { icon: FileCode, color: "text-yellow-500" },
    ts: { icon: FileCode, color: "text-blue-500" },
    json: { icon: FileCode, color: "text-gray-500" },
    html: { icon: FileCode, color: "text-orange-500" },

    // Archives
    zip: { icon: FileArchive, color: "text-yellow-500" },
    rar: { icon: FileArchive, color: "text-yellow-500" },
  };

  const { icon: IconComponent, color } = fileConfig[
    extension as FileExtension
  ] || {
    icon: File,
    color: "text-gray-400",
  };
  const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(extension);
  return (
    <div className="group rounded-lg border border-gray-200 bg-white p-4 duration-200">
      <div className="flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-4">
          {isImage && showImagePreview ? (
            <>
              <div className="relative h-14 w-14 overflow-hidden rounded-md">
                <Image
                  src={showImagePreviewUrl || ""}
                  alt="Company Logo"
                  width={0}
                  height={0}
                  quality={100}
                  style={{
                    height: "100%",
                    width: "100%",
                  }}
                  sizes="100vw"
                  className="object-cover"
                  unoptimized
                />
              </div>
            </>
          ) : (
            <>
              <div
                className={`rounded-lg bg-gray-50 p-3 transition-colors group-hover:bg-blue-50 ${color}`}
              >
                <IconComponent className="h-6 w-6" />
              </div>
            </>
          )}
          <div className="min-w-0">
            <h3 className="truncate text-sm font-medium text-gray-900">
              {displayName}
            </h3>
            <div className="flex items-center gap-2">
              {size && (
                <>
                  <span className="text-xs text-gray-500">{size}</span>
                  <span className="text-gray-400">•</span>
                </>
              )}
              <span className="text-xs text-gray-500 uppercase">
                {extension}
              </span>
            </div>
          </div>
        </div>

        {showDeleteButton && removeFile ? (
          <>
            <div
              onClick={removeFile}
              className="cursor-pointer rounded-full p-2 text-red-400 transition-colors hover:bg-red-50 hover:text-red-500"
            >
              <RoundedCross />
            </div>
          </>
        ) : (
          <>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={url}
              download={displayName}
              className="rounded-full p-2 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-500"
              aria-label={`Download ${displayName}`}
            >
              <Download className="h-5 w-5" />
            </a>
          </>
        )}
      </div>
    </div>
  );
};

export default FilePreview;
